// IchimokuCloudPrimitive — lightweight-charts v5 ISeriesPrimitive that fills
// the Ichimoku Kumo (cloud) between Senkou Span A and Senkou Span B series.
//
// Why a custom primitive: v5 has no native between-lines fill. AreaSeries fills
// from a line to the pane bottom, and stacking two AreaSeries does not produce
// a true interleaved cloud. The primitive owns its own canvas pass and reads
// both series' projected pixel coordinates each frame, so the fill stays correct
// during pan/zoom.
//
// Redraw protocol (CRITICAL — see conflict detector finding in tasks.md):
//   - The renderer is invoked automatically on pan/zoom by lightweight-charts.
//   - On data-only changes (caller-side setData), call `requestUpdate` captured
//     from the SeriesAttachedParameter in attached(). Do NOT call
//     `chart.applyOptions()` to force redraws.

import type {
  ISeriesPrimitive,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import { DisplacedPoint } from '../../utils/ichimokuDisplacement';
import { ichimokuPalette } from './ichimokuPalette';

interface RenderableSegment {
  x0: number;
  x1: number;
  yA0: number;
  yA1: number;
  yB0: number;
  yB1: number;
  bullish: boolean;
}

class IchimokuCloudRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly segments: RenderableSegment[],
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const xRatio = scope.horizontalPixelRatio;
      const yRatio = scope.verticalPixelRatio;
      for (const seg of this.segments) {
        ctx.beginPath();
        ctx.moveTo(seg.x0 * xRatio, seg.yA0 * yRatio);
        ctx.lineTo(seg.x1 * xRatio, seg.yA1 * yRatio);
        ctx.lineTo(seg.x1 * xRatio, seg.yB1 * yRatio);
        ctx.lineTo(seg.x0 * xRatio, seg.yB0 * yRatio);
        ctx.closePath();
        ctx.fillStyle = seg.bullish ? ichimokuPalette.bullishCloud : ichimokuPalette.bearishCloud;
        ctx.fill();
      }
    });
  }
}

class IchimokuCloudPaneView implements IPrimitivePaneView {
  constructor(private readonly source: IchimokuCloudPrimitive) {}

  // Render below price lines but above the chart background.
  zOrder() {
    return 'bottom' as const;
  }

  renderer(): IPrimitivePaneRenderer | null {
    const segs = this.source.computeSegments();
    if (segs.length === 0) return null;
    return new IchimokuCloudRenderer(segs);
  }
}

export class IchimokuCloudPrimitive implements ISeriesPrimitive<Time> {
  private spanASeries: ISeriesApi<'Line'> | null = null;
  private spanBSeries: ISeriesApi<'Line'> | null = null;
  private spanA: DisplacedPoint[] = [];
  private spanB: DisplacedPoint[] = [];
  private chartRef: SeriesAttachedParameter<Time>['chart'] | null = null;
  private requestUpdate: (() => void) | null = null;
  private readonly views: IchimokuCloudPaneView[];

  constructor() {
    this.views = [new IchimokuCloudPaneView(this)];
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chartRef = param.chart;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.chartRef = null;
    this.requestUpdate = null;
  }

  setSeries(spanA: ISeriesApi<'Line'>, spanB: ISeriesApi<'Line'>): void {
    this.spanASeries = spanA;
    this.spanBSeries = spanB;
  }

  setData(spanA: DisplacedPoint[], spanB: DisplacedPoint[]): void {
    this.spanA = spanA;
    this.spanB = spanB;
    if (this.requestUpdate) {
      this.requestUpdate();
    }
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return this.views;
  }

  // Walk paired Span A / Span B points, project to pixel coordinates, and emit
  // one quadrilateral per consecutive pair colored bullish/bearish by the
  // averaged A-vs-B comparison over that segment. A more accurate renderer
  // would split at crossovers — left as a follow-up if visual artifacts surface.
  computeSegments(): RenderableSegment[] {
    if (!this.chartRef || !this.spanASeries || !this.spanBSeries) return [];
    if (this.spanA.length < 2 || this.spanB.length < 2) return [];

    // Pair points by index. Both series have the same time domain because they
    // are written from the same source rows in CandlestickChart.
    const n = Math.min(this.spanA.length, this.spanB.length);
    const timeScale = this.chartRef.timeScale();
    const segs: RenderableSegment[] = [];

    for (let i = 0; i < n - 1; i++) {
      const aPoint0 = this.spanA[i];
      const aPoint1 = this.spanA[i + 1];
      const bPoint0 = this.spanB[i];
      const bPoint1 = this.spanB[i + 1];

      const x0 = timeScale.timeToCoordinate(aPoint0.time);
      const x1 = timeScale.timeToCoordinate(aPoint1.time);
      const yA0 = this.spanASeries.priceToCoordinate(aPoint0.value);
      const yA1 = this.spanASeries.priceToCoordinate(aPoint1.value);
      const yB0 = this.spanBSeries.priceToCoordinate(bPoint0.value);
      const yB1 = this.spanBSeries.priceToCoordinate(bPoint1.value);

      if (x0 === null || x1 === null || yA0 === null || yA1 === null || yB0 === null || yB1 === null) {
        continue;
      }

      const avgA = (aPoint0.value + aPoint1.value) / 2;
      const avgB = (bPoint0.value + bPoint1.value) / 2;
      segs.push({
        x0, x1, yA0, yA1, yB0, yB1,
        bullish: avgA >= avgB,
      });
    }
    return segs;
  }
}
